import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiBell, HiCheck, HiXMark } from 'react-icons/hi2';
import clsx from 'clsx';
import toast from '@/lib/notify';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiResponse, PaginationMeta, UserNotification } from '@amuster/types';
import { getErrorMessage } from '@/lib/api';
import { connectSocket, getSocket } from '@/lib/socket';
import {
  useMarkNotificationRead,
  useNotificationsInbox,
  useNotificationsUnreadCount,
  useReadAllNotifications,
} from '@/hooks/useQueryHooks';

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { data: unreadCountResp } = useNotificationsUnreadCount();
  const unreadCount = unreadCountResp?.data?.count ?? 0;

  const inboxQuery = useMemo(() => ({ page: 1, limit: 20 }), []);
  const { data: inboxResp, isLoading: inboxLoading } = useNotificationsInbox(inboxQuery, {
    enabled: open,
  });

  const notifications = inboxResp?.data ?? [];

  const markReadMutation = useMarkNotificationRead();
  const readAllMutation = useReadAllNotifications();

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Realtime: notifications:new
  useEffect(() => {
    const socket = getSocket() ?? connectSocket();
    if (!socket) return;

    const onNew = (payload: unknown) => {
      const notification = (payload as { notification?: UserNotification } | null)?.notification;
      if (!notification?.id) return;

      // Update unread count (unread-only badge)
      qc.setQueryData<ApiResponse<{ count: number }>>(
        ['notifications', 'unread-count'],
        (old) => {
          const current = old?.data?.count ?? 0;
          return { ...(old ?? { success: true, data: { count: 0 } }), data: { count: current + 1 } };
        }
      );

      // Prepend to first-page inbox caches (avoid duplicates)
      const existing = qc.getQueriesData<ApiResponse<UserNotification[]> & { meta?: PaginationMeta }>({
        queryKey: ['notifications', 'inbox'],
      });

      for (const [key, data] of existing) {
        const query = Array.isArray(key) ? (key[2] as { page?: number; unread?: boolean } | undefined) : undefined;
        if (query?.page && query.page !== 1) continue;
        if (!data?.data) continue;

        const already = data.data.some((n) => n.id === notification.id);
        if (already) continue;

        const maxItems = Math.max(1, data.data.length);
        qc.setQueryData(key, {
          ...data,
          data: [notification, ...data.data].slice(0, maxItems),
        });
      }
    };

    socket.on('notifications:new', onNew);
    return () => {
      socket.off('notifications:new', onNew);
    };
  }, [qc]);

  const markReadAndNavigate = async (n: UserNotification) => {
    try {
      const wasUnread = !n.read_at;
      await markReadMutation.mutateAsync(n.id);

      if (wasUnread) {
        qc.setQueryData<ApiResponse<{ count: number }>>(
          ['notifications', 'unread-count'],
          (old) => {
            const current = old?.data?.count ?? 0;
            return { ...(old ?? { success: true, data: { count: 0 } }), data: { count: Math.max(0, current - 1) } };
          }
        );
      }

      setOpen(false);
      if (n.link_path) {
        navigate(n.link_path);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const markAllRead = async () => {
    try {
      await readAllMutation.mutateAsync();
      qc.setQueryData<ApiResponse<{ count: number }>>(
        ['notifications', 'unread-count'],
        (old) => ({ ...(old ?? { success: true, data: { count: 0 } }), data: { count: 0 } })
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <HiBell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold leading-none text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] origin-top-right rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Notifications</p>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  disabled={readAllMutation.isPending}
                  className="rounded-lg px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                aria-label="Close notifications"
              >
                <HiXMark className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-auto">
            {inboxLoading ? (
              <div className="px-4 py-6 text-sm text-gray-500">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">No notifications</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {notifications.map((n) => {
                  const unread = !n.read_at;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => void markReadAndNavigate(n)}
                        className={clsx(
                          'w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors',
                          unread && 'bg-blue-50/60'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {unread ? (
                              <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                            ) : (
                              <HiCheck className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className={clsx('text-sm font-medium truncate', unread ? 'text-gray-900' : 'text-gray-700')}>
                                {n.title}
                              </p>
                              <p className="text-[11px] text-gray-400 whitespace-nowrap">{formatWhen(n.created_at)}</p>
                            </div>
                            <p className={clsx('mt-0.5 text-sm', unread ? 'text-gray-700' : 'text-gray-500')}>
                              {n.message}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

